import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { STALE } from "@/lib/api/query-client";
import { eventPlansApi, eventsApi, physiciansApi, type PhysicianListParams } from "./events.api";
import type { EventFilePurpose, EventFilters } from "./events.types";

/**
 * `STALE.record`: etkinlik gün içinde birkaç kez değişen bir kayıt — ne kuyruk ne katalog.
 * Pano da aynı: özet, altındaki kayıtlardan türetiliyor ve onlar değişince eskiyor.
 *
 * ANAHTARLAR `["events", …]` İLE BAŞLIYOR: kart bir yazmadan sonra `["events"]` önekini
 * geçersiz kılıyor, böylece bütçe özeti, kalem listesi ve pano birlikte tazeleniyor.
 */
/** Filtrenin sayfadan bağımsız kısmı — iki sorgu aynı listenin iki sayfası mı. */
function withoutPaging(filters: { page?: number | undefined; limit?: number | undefined }): string {
  const { page: _page, limit: _limit, ...rest } = filters;
  return JSON.stringify(Object.entries(rest).sort(([a], [b]) => a.localeCompare(b)));
}

/**
 * ÖNCEKİ VERİ YALNIZ SAYFA ADIMINDA (pazarlama tasarım turu, 31 Ekim 2026). `keepPreviousData` — ve
 * uygulamanın varsayılanı (`query-client.ts`) — filtre değişince de eski satırları tutuyordu: yıl
 * 2026'dan 2024'e çevrilince 2026'nın kongreleri ve BÜTÇELERİ yeni filtrenin altında duruyordu
 * (tarayıcıda görüldü). `loading-and-skeleton.md`: «old figures under a new filter are a lie».
 * Artık yalnız sayfa ve sayfa boyu değişince eski sayfa duruyor; şirket, filtre ya da arama
 * değişince liste iskeletine dönüyor.
 */
export function eventListQuery(legalEntityId: string | null, filters: EventFilters) {
  return queryOptions({
    queryKey: ["events", "list", legalEntityId, filters] as const,
    queryFn: () => eventsApi.list({ ...filters, legalEntityId: legalEntityId ?? undefined }),
    enabled: legalEntityId !== null,
    staleTime: STALE.record,
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      if (!previous || !key || key[2] !== legalEntityId) return undefined;
      return withoutPaging(key[3]) === withoutPaging(filters) ? keepPreviousData(previous) : undefined;
    },
  });
}

export function eventQuery(id: string | null) {
  return queryOptions({
    queryKey: ["events", "detail", id] as const,
    queryFn: () => eventsApi.get(id ?? ""),
    enabled: id !== null && id !== "",
    staleTime: STALE.record,
  });
}

export function eventDashboardQuery(legalEntityId: string | null, year: number | undefined) {
  return queryOptions({
    queryKey: ["events", "dashboard", legalEntityId, year ?? null] as const,
    queryFn: () =>
      eventsApi.dashboard({
        ...(legalEntityId ? { legalEntityId } : {}),
        ...(year ? { year } : {}),
      }),
    enabled: legalEntityId !== null,
    staleTime: STALE.record,
  });
}

export function budgetSummaryQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "budget-summary", eventId] as const,
    queryFn: () => eventsApi.budgetSummary(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

/** Kalemler — her biri planlanan, ödenen ve kalanıyla (M2). */
export function budgetLinesQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "budget-lines", eventId] as const,
    queryFn: () => eventsApi.budgetLines(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

/** Bir kalemin ödemeleri — kalem açıldığında okunuyor, liste her kalem için istek atmıyor. */
export function budgetPaymentsQuery(budgetLineId: string | null) {
  return queryOptions({
    queryKey: ["events", "budget-payments", budgetLineId] as const,
    queryFn: () => eventsApi.budgetPayments(budgetLineId ?? ""),
    enabled: budgetLineId !== null && budgetLineId !== "",
    staleTime: STALE.record,
  });
}

export function sponsorshipItemsQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "items", eventId] as const,
    queryFn: () => eventsApi.sponsorshipItems(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

/** Kongrenin bir yuvasındaki ekler — sponsorluk dosyası ya da stant planı (M1, M9). */
export function eventAttachmentsQuery(eventId: string | null, purpose: EventFilePurpose) {
  return queryOptions({
    queryKey: ["events", "attachments", eventId, purpose] as const,
    queryFn: () => eventsApi.attachments(eventId ?? "", purpose),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

/** Bir ödemenin dekontları (M2). */
export function paymentAttachmentsQuery(paymentId: string | null) {
  return queryOptions({
    queryKey: ["events", "payment-attachments", paymentId] as const,
    queryFn: () => eventsApi.paymentAttachments(paymentId ?? ""),
    enabled: paymentId !== null && paymentId !== "",
    staleTime: STALE.record,
  });
}

/** Yuva kataloğu — kurulumda nadiren değişiyor; tür ve boyut sınırını sunucudan okumak için. */
export function attachmentPurposesQuery(entityType: string) {
  return queryOptions({
    queryKey: ["files", "purposes", entityType] as const,
    queryFn: () => eventsApi.attachmentPurposes(entityType),
    staleTime: STALE.reference,
  });
}

export function eventSessionsQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "sessions", eventId] as const,
    queryFn: () => eventsApi.sessions(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

export function eventMaterialsQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "materials", eventId] as const,
    queryFn: () => eventsApi.materials(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

export function eventParticipantsQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "participants", eventId] as const,
    queryFn: () => eventsApi.participants(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

export function eventTasksQuery(eventId: string | null) {
  return queryOptions({
    queryKey: ["events", "tasks", eventId] as const,
    queryFn: () => eventsApi.tasks(eventId ?? ""),
    enabled: eventId !== null && eventId !== "",
    staleTime: STALE.record,
  });
}

export function eventPlansQuery(year: number | undefined) {
  return queryOptions({
    queryKey: ["events", "plans", year ?? null] as const,
    queryFn: () => eventPlansApi.list({ ...(year ? { year } : {}), limit: 50 }),
    staleTime: STALE.record,
  });
}

/**
 * Bir şirketin yıllık planları — kongre düzenleme formunun «Yıllık plan» seçicisi (M10).
 * Kongre YALNIZ kendi şirketinin planına bağlanabiliyor; liste o yüzden şirkete göre.
 */
/** Planın geçmişi — plan sayfasının «Geçmiş» bölümü (31 Ekim 2026, P-D). */
export function planHistoryQuery(planId: string | null) {
  return queryOptions({
    queryKey: ["events", "plans", "history", planId] as const,
    queryFn: () => eventPlansApi.history(planId ?? ""),
    enabled: planId !== null && planId !== "",
    staleTime: STALE.record,
  });
}

export function companyPlansQuery(legalEntityId: string | null) {
  return queryOptions({
    queryKey: ["events", "plans", "company", legalEntityId] as const,
    queryFn: () => eventPlansApi.list({ legalEntityId: legalEntityId ?? "", limit: 50 }),
    enabled: legalEntityId !== null && legalEntityId !== "",
    staleTime: STALE.record,
  });
}

/**
 * Hekim listesi — önceki veri yine YALNIZ SAYFA ADIMINDA (31 Ekim 2026; bkz. `eventListQuery`): arama,
 * şehir, branş ya da sıralama değişince eski hekimler yeni filtrenin altında durmuyor, liste iskelete dönüyor.
 */
export function physicianListQuery(params: PhysicianListParams & { page: number; limit: number }) {
  return queryOptions({
    queryKey: ["physicians", "list", params] as const,
    queryFn: () => physiciansApi.list(params),
    staleTime: STALE.record,
    placeholderData: (previous, previousQuery) => {
      const before = previousQuery?.queryKey[2];
      if (!previous || !before) return undefined;
      return withoutPaging(before) === withoutPaging(params) ? keepPreviousData(previous) : undefined;
    },
  });
}
